import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Stack,
  Typography,
  Divider,
} from "@financeable-com-au/financeable-ui";
import { AUTH_REQUEST_KEYS, authV2Selector } from "@/store/slices/authV2Slice";
import { useMagicOtpCoolDown } from "@/hooks";
import { AuthCard } from "@components/AuthCard";

interface PasswordStepProps {
  email: string;
  onBack: () => void;
  onPasswordSubmit: (password: string) => void;
  onMagicSubmit: () => void;
}

const PasswordStep = ({ email, onBack, onPasswordSubmit, onMagicSubmit }: PasswordStepProps) => {
  const authV2 = useSelector(authV2Selector);
  const passwordLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.passwordLogin]);
  const magicLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.magicSend]);
  const { magicOtpCooldownActive, magicOtpCooldownRemainingSeconds } = useMagicOtpCoolDown(email);
  const magicOtpCooldownUnit = magicOtpCooldownRemainingSeconds === 1 ? "second" : "seconds";
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      password: "",
    },
    onSubmit: (values) => {
      onPasswordSubmit(values.password);
    },
    validationSchema: yup.object({
      password: yup.string().required("Please enter your password."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  const navigateToResetPassword = () => {
    navigate("/auth/reset-password", { state: { email } });
  };

  return (
    <>
      <Stack textAlign="center" rowGap={1}>
        <Typography level="title-lg">Sign in</Typography>
        <Typography level="body-lg" textColor="neutral.600">
          Enter your password, or we can send a one-time code to your email.
        </Typography>
      </Stack>
      <AuthCard onSubmit={() => formik.handleSubmit()}>
        <Stack rowGap={4}>
          <Stack>
            <Typography mb={2}>Email</Typography>
            <Typography level="body-md" textColor="neutral.600" bold>
              {email}
            </Typography>
          </Stack>
          <Stack rowGap={6}>
            <FormControl error={!!formik.errors.password}>
              <Stack flexDirection="row" alignItems="center" mb={2} justifyContent="space-between">
                <FormLabel size="md">Password</FormLabel>
                <Button variant="inline" onClick={navigateToResetPassword}>
                  Forgot password?
                </Button>
              </Stack>
              <Input
                autoFocus
                placeholder="Your password"
                type="password"
                value={formik.values.password}
                onChange={(event) => {
                  formik.setFieldValue("password", event.target.value);
                  if (formik.errors.password) {
                    formik.setFieldError("password", undefined);
                  }
                }}
                size="lg"
              />
              {formik.errors.password ? (
                <FormHelperText>
                  <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                  {formik.errors.password}
                </FormHelperText>
              ) : null}
            </FormControl>
            <Stack direction="column" rowGap={6}>
              <Button
                size="lg"
                variant="primary"
                loading={passwordLoading}
                endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
                type="submit"
              >
                Sign in
              </Button>
              <Divider>Or</Divider>
              <Stack flexDirection="column" rowGap={2} alignItems="center">
                <Button
                  size="lg"
                  variant="secondary"
                  loading={magicLoading}
                  disabled={magicOtpCooldownActive}
                  onClick={onMagicSubmit}
                  endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
                  fullWidth
                >
                  Email sign-in code
                </Button>
                {magicOtpCooldownActive ? (
                  <Typography level="body-xs" textColor="neutral.600">
                    Email sign-in code is available in {magicOtpCooldownRemainingSeconds} {magicOtpCooldownUnit}.
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </AuthCard>
      <Button
        startDecorator={<ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />}
        variant="inline"
        intent="neutral"
        size="lg"
        onClick={onBack}
      >
        Go back
      </Button>
    </>
  );
};

export { PasswordStep };
